import * as geminiService from './geminiService';

/**
 * Sends a prompt to generate an image using F.R.A.N.C.A. AI.
 * @param {string} token - The user's authentication token.
 * @param {string} prompt - The text prompt for the image.
 * @param {string} model - The AI model to use (optional).
 * @returns {Promise<object>} - A promise that resolves to the API response with images array.
 */
export const generateImage = async (token, prompt, model) => {
    return await geminiService.generateImageFromText(token, prompt, model);
};

/**
 * Generates multiple explanatory visuals intended for inline chat teaching mode.
 * @param {string} token - The user's authentication token.
 * @param {Array<{section_title: string, visual_prompt: string, caption?: string, model?: string}>} items
 * @returns {Promise<object>} - Section-wise visual generation response.
 */
export const generateInlineExplanationVisuals = async (token, items) => {
    return await geminiService.generateExplanationVisuals(token, items);
};

/**
 * Picks the generation model for inline visual explanations.
 * Defaults to fast for responsiveness, upgrades to advanced for high-detail prompts.
 * @param {string} prompt - The generated visual prompt.
 * @returns {'fast'|'advanced'} - Recommended model.
 */
export const selectVisualExplanationModel = (prompt = '') => {
    const highDetailHints = /\b(detailed|photorealistic|cinematic|ultra|8k|render|texture|lighting|high[- ]detail)\b/i;
    return highDetailHints.test(prompt) ? 'advanced' : 'fast';
};

/**
 * Legacy function for backward compatibility - generates image and returns first result
 * in the old format { image_b64, prompt_used }
 * @param {string} token - The user's authentication token.
 * @param {string} prompt - The text prompt for the image.
 * @returns {Promise<object>} - A promise that resolves to { image_b64, prompt_used }.
 */
export const generateImageLegacy = async (token, prompt) => {
    const response = await geminiService.generateImageFromText(token, prompt);
    return {
        image_b64: response.images?.[0]?.data || null,
        prompt_used: prompt
    };
};

/**
 * Gets available F.R.A.N.C.A. AI models
 * @param {string} token - The user's authentication token
 * @returns {Promise<object>} - Available models and service status
 */
export const getAvailableModels = async (token) => {
    return await geminiService.getFrancaModels(token);
};

/**
 * Edits an existing image with text prompt
 * @param {string} token - The user's authentication token
 * @param {string} prompt - The edit instruction
 * @param {File} imageFile - The image file to edit
 * @param {string} model - The AI model to use (optional)
 * @returns {Promise<object>} - Response with edited images
 */
export const editImage = async (token, prompt, imageFile, model) => {
    return await geminiService.editImageWithText(token, prompt, imageFile, model);
};

/**
 * Generates image from multiple input images
 * @param {string} token - The user's authentication token
 * @param {string} prompt - The generation prompt
 * @param {File[]} imageFiles - Array of image files (up to 3)
 * @param {string} model - The AI model to use (optional)
 * @returns {Promise<object>} - Response with generated images
 */
export const generateFromMultipleImages = async (token, prompt, imageFiles, model) => {
    return await geminiService.generateFromMultipleImages(token, prompt, imageFiles, model);
};

/**
 * Generates an enhanced "magic" prompt from a simple user idea.
 * @param {string} token - The user's authentication token.
 * @param {string} simplePrompt - The user's basic prompt text.
 * @param {string} length - The desired length of the enhanced prompt ("short" | "medium" | "long"). Defaults to "medium".
 * @returns {Promise<string>} - The enhanced prompt text.
 */
export const getMagicPrompt = async (token, simplePrompt, length = 'medium') => {
    try {
        const response = await geminiService.getMagicPrompt(token, simplePrompt, length);
        return response;
    } catch (error) {
        console.error("Error fetching magic prompt:", error);
        throw error.response?.data || new Error("Failed to generate magic prompt.");
    }
};

/**
 * Edits an image using magic prompt enhancement in a single operation
 * @param {string} token - The user's authentication token
 * @param {string} simplePrompt - The user's simple editing prompt
 * @param {File} imageFile - The image file to edit
 * @param {string} model - The AI model to use (optional)
 * @param {string} length - The desired length of the enhanced prompt ("short" | "medium" | "long"). Defaults to "medium".
 * @returns {Promise<object>} - Response with edited images and metadata
 */
export const magicEditImage = async (token, simplePrompt, imageFile, model, length = 'medium') => {
    return await geminiService.magicEditImage(token, simplePrompt, imageFile, model, length);
};

/**
 * Fetches user's image generation history
 * @param {string} token - The user's authentication token
 * @param {number} limit - Maximum number of items to return (default: 5)
 * @param {number} sinceMinutes - Filter history from the last N minutes (default: 60)
 * @returns {Promise<Array>} - Array of image generation history items
 */
export const getImageHistory = async (token, limit = 5, sinceMinutes = 60) => {
    return await geminiService.getImageHistory(token, limit, sinceMinutes);
};

/**
 * Gets info about generated media (count, size, retention policy).
 * @param {string} token
 * @returns {Promise<object>}
 */
export const getMediaInfo = async (token) => {
    return await geminiService.getMediaInfo(token);
};

/**
 * Lists individual media assets with preview URLs, sizes, timestamps.
 * @param {string} token
 * @returns {Promise<{items: Array, total: number}>}
 */
export const listMediaAssets = async (token) => {
    return await geminiService.listMediaAssets(token);
};

/**
 * Deletes a single generated media file by filename.
 * @param {string} token
 * @param {string} filename
 * @returns {Promise<object>}
 */
export const deleteSingleMedia = async (token, filename) => {
    return await geminiService.deleteSingleMedia(token, filename);
};

/**
 * Deletes all generated media files on the server.
 * @param {string} token
 * @returns {Promise<object>}
 */
export const clearAllGeneratedMedia = async (token) => {
    return await geminiService.clearAllGeneratedMedia(token);
};